import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ApiError, api } from "../../api";
import type { IssueDetail } from "../../types";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Field, FieldDescription, FieldLabel } from "../ui/field";
import { Spinner } from "../ui/spinner";
import { Textarea } from "../ui/textarea";

export function QuickFixDialog({ issue, onChanged }: { issue: IssueDetail; onChanged: () => void }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState("");
  const quickFix = useMutation({
    mutationFn: async () => {
      const body = request.trim();
      await api.addComment(issue.identifier, { body });
      await api.delegateIssue(issue.identifier, {
        instructions: body,
        startAgentId: "quick-fix",
      });
    },
    onSuccess: () => {
      setRequest("");
      setOpen(false);
      toast.success("Quick fix sent to Skynet");
      onChanged();
      void queryClient.invalidateQueries({ queryKey: ["issue-comments", issue.identifier] });
      void queryClient.invalidateQueries({ queryKey: ["tasks"] });
      void queryClient.invalidateQueries({ queryKey: ["state"] });
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Quick fix failed to start"),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!quickFix.isPending) setOpen(next);
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-full">
          <Sparkles className="size-4" /> Quick fix
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <form
          className="grid gap-6"
          onSubmit={(event) => {
            event.preventDefault();
            if (request.trim() && !quickFix.isPending) quickFix.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>Request a quick fix</DialogTitle>
            <DialogDescription>
              Describe one small change. Skynet posts it as a comment, updates the existing pull request, then
              sends only that change to the Verifier.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="quick-fix-request">Change request</FieldLabel>
            <Textarea
              id="quick-fix-request"
              value={request}
              onChange={(event) => setRequest(event.target.value)}
              placeholder="For example: change the empty-state heading to “No leases yet”."
              rows={4}
              autoFocus
            />
            <FieldDescription>Keep it narrow. Use a regular work order for a larger change.</FieldDescription>
          </Field>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={quickFix.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!request.trim() || quickFix.isPending}>
              {quickFix.isPending && <Spinner />}
              Send quick fix
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
